import { RequestStatus } from '@transcend-io/privacy-types';
import React from 'react';
import { MessageDescriptor } from 'react-intl';

import { DownloadZipProps } from '../../hooks';
import { RequestPreviewProps } from './index';
import { requestPreviewMessages } from './messages';
import { CustomStatusBlockProps, StatusBlock } from './StatusBlock';

/**
 * Input props for components
 */
type CustomStatusBlockPropsInput = {
  [k in keyof CustomStatusBlockProps]: CustomStatusBlockProps[k] extends MessageDescriptor
    ? MessageDescriptor | ((props: RequestPreviewProps) => MessageDescriptor)
    : CustomStatusBlockProps[k];
};

/**
 * Status block props without download zip props
 */
type WithoutDownloadZipProps = Omit<
  CustomStatusBlockPropsInput,
  keyof DownloadZipProps
>;

const createStatusBlock = ({
  title,
  description,
  ...rest
}: WithoutDownloadZipProps): React.FC<
  React.PropsWithChildren<RequestPreviewProps>
> => {
  /**
   * Status block
   *
   * @param props - The RequestPreview props
   */
  function CustomStatusBlock(props: RequestPreviewProps): JSX.Element {
    return (
      <StatusBlock
        {...props}
        title={typeof title === 'function' ? title(props) : title}
        description={
          typeof description === 'function' ? description(props) : description
        }
        {...rest}
      />
    );
  }
  return CustomStatusBlock;
};

const NewRequest = createStatusBlock({
  title: requestPreviewMessages.requestMadeTitle,
  icon: 'request-sent',
  description: requestPreviewMessages.requestMadeDescription,
});

const FailedVerification = createStatusBlock({
  title: requestPreviewMessages.failedVerificationTitle,
  icon: 'request-failure',
  description: requestPreviewMessages.failedVerificationDescription,
});

const Processing = createStatusBlock({
  title: requestPreviewMessages.processingTitle,
  icon: 'request-processing',
  description: requestPreviewMessages.processingDescription,
});

const OnHold = createStatusBlock({
  title: requestPreviewMessages.onHoldTitle,
  icon: 'request-processing',
  description: requestPreviewMessages.onHoldDescription,
});

const Delayed = createStatusBlock({
  title: requestPreviewMessages.delayedTitle,
  icon: 'request-processing',
  description: requestPreviewMessages.delayedDescription,
});

const Ready = createStatusBlock({
  title: ({ canDownload }) =>
    canDownload
      ? requestPreviewMessages.downloadableTitle
      : requestPreviewMessages.completedTitle,
  icon: 'request-data-ready',
  description: ({ canDownload }) =>
    canDownload
      ? requestPreviewMessages.downloadableDescription
      : requestPreviewMessages.completedDescription,
});

const Canceled = createStatusBlock({
  title: requestPreviewMessages.canceledTitle,
  icon: 'request-canceled',
  description: requestPreviewMessages.canceledDescription,
});

const Erasing = createStatusBlock({
  title: requestPreviewMessages.secondaryTitle,
  icon: 'request-erasing',
  description: requestPreviewMessages.secondaryDescription,
});

const Erased = createStatusBlock({
  title: requestPreviewMessages.secondaryCompletedTitle,
  icon: 'request-erased',
  description: requestPreviewMessages.secondaryCompletedDescription,
});

const Revoked = createStatusBlock({
  title: requestPreviewMessages.revokedTitle,
  icon: 'request-failure',
  description: requestPreviewMessages.revokedDescription,
});

export const REQUEST_PREVIEWS: {
  [status in RequestStatus]: React.ComponentType<
    React.PropsWithChildren<RequestPreviewProps>
  >;
} = {
  [RequestStatus.RequestMade]: NewRequest,
  [RequestStatus.FailedVerification]: FailedVerification,
  [RequestStatus.Enriching]: Processing,
  [RequestStatus.OnHold]: OnHold, // TODO should not show this on frontend
  [RequestStatus.Waiting]: Processing,
  [RequestStatus.Compiling]: Processing,
  [RequestStatus.Approving]: Processing,
  [RequestStatus.Delayed]: Delayed,
  [RequestStatus.Completed]: Ready,
  [RequestStatus.Downloadable]: Ready,
  [RequestStatus.ViewCategories]: Ready,
  [RequestStatus.Canceled]: Canceled,
  [RequestStatus.Secondary]: Erasing,
  [RequestStatus.SecondaryApproving]: Erasing,
  [RequestStatus.SecondaryCompleted]: Erased,
  [RequestStatus.Revoked]: Revoked,
};
